'use client';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Button, ConfirmDialog } from '@miso/design-system';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import { ROOM_TYPES, type RoomTypeValue } from '@/entities/visit-counsel/model/house-sketch.types';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import { sortAdditionalSpaceObservingSessions } from '@/features/visit-counsel/counsel-progress/model/additionalSpaceObservingOrder';
import {
  AddRoomNameSheet,
  AddRoomTypeSheet,
  type AddRoomTypeOption,
} from '@/features/visit-counsel/room-add-sheets';
import {
  buildObservingPickerSections,
  type ObservingPickerEntry,
  type ObservingPickerSection,
} from '../model/buildObservingPickerSections';
import {
  canDeleteObservingRoom,
  describeDeleteBlockedReason,
  resolveRoomIdForDelete,
} from '../model/canDeleteObservingRoom';

// ── 폴링 정책 (useSketchingRoomMutations 와 동일) ──────────────────────────────
const POLL_INTERVAL_MS = 400;
const POLL_MAX_MS = 10_000;

// ── "+ 방 추가" 시 사용자에게 보여줄 타입 후보 ─────────────────────────────────
// 현관(ENTRANCE)은 picker 에서 제외되며, 사용자가 직접 추가할 항목이 아니라 제외한다.
const ADD_ROOM_TYPE_OPTIONS: ReadonlyArray<AddRoomTypeOption<RoomTypeValue>> = ROOM_TYPES.filter(
  (r) => r.value !== 'ENTRANCE'
);

// ── Props ─────────────────────────────────────────────────────────────────────

export interface RoomEditFlowAdapterProps {
  visitCounselId: string;
  counsel: VisitCounselView;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  /**
   * SWR 캐시를 갱신해 새 counsel 을 가져오는 콜백.
   * 호출하면 부모가 새 `counsel` prop 으로 본 어댑터를 다시 렌더한다.
   *
   * 응답이 사용 가능하면 새 counsel 을 그대로 반환해야 — 본 어댑터의 폴링 루프가
   * 변경(추가/삭제)이 프로젝션에 반영됐는지 검사한다.
   */
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  /** 사용자가 picker 로 돌아갈 때 호출. */
  onBack: () => void;
}

// ── Adapter ───────────────────────────────────────────────────────────────────

/**
 * picker 화면에서 "방 수정하기" 를 누르면 진입하는 방 추가/삭제 어댑터.
 *
 * 정책:
 *   - 표시 대상: picker 와 동일한 분류 (`buildObservingPickerSections`).
 *   - 삭제 가능 여부: `canDeleteObservingRoom` — 옵저빙 status === 'REQUESTED'
 *     이며 짐/영상이 비어있을 때만. 한 번이라도 짐기록·영상촬영이 시작된 방은
 *     자물쇠 아이콘 + "기록을 시작했어요" 캡션으로 잠긴 상태를 명시한다.
 *   - 추가: `addSketchingRoom` (sketching 상태가 STARTED 일 때만 백엔드가 허용).
 *   - 삭제: `removeSketchingRoom` (옵저빙이 REQUESTED 일 때만 백엔드가 허용).
 *
 * 방 추가/삭제 후에는 짧은 폴링으로 프로젝션이 반영될 때까지 기다린 뒤 부모에게
 * 새 counsel 을 전달한다. 폴링 시간 한계에 도달하면 마지막 응답을 그대로 전달한다.
 */
export function RoomEditFlowAdapter({
  visitCounselId,
  counsel,
  showToast,
  refetch,
  onBack,
}: RoomEditFlowAdapterProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const sketchRooms = counsel.sessions?.sketching?.rooms ?? [];
  const roomObserving = counsel.sessions?.roomObserving ?? [];
  const additionalSpaceObserving = useMemo(
    () => sortAdditionalSpaceObservingSessions(counsel.sessions?.additionalSpaceObserving ?? []),
    [counsel.sessions?.additionalSpaceObserving]
  );

  const sections: ObservingPickerSection[] = useMemo(
    () =>
      buildObservingPickerSections({
        rooms: sketchRooms,
        roomObserving,
        additionalSpaceObserving,
      }),
    [sketchRooms, roomObserving, additionalSpaceObserving]
  );

  // ── 상태 ───────────────────────────────────────────────────────────────────
  const [removingRoomIds, setRemovingRoomIds] = useState<Set<string>>(new Set());
  const [removeConfirmEntry, setRemoveConfirmEntry] = useState<ObservingPickerEntry | null>(null);

  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [addType, setAddType] = useState<RoomTypeValue | null>(null);
  const [nameSheetOpen, setNameSheetOpen] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [isAddingRoom, setIsAddingRoom] = useState(false);

  // sketching 이 STARTED 일 때만 backend 가 추가를 허용한다.
  // (COMPLETE 단계에 도달했다는 것은 영상까지 끝났다는 의미라 + 추가는 의도적이지 않음)
  const canAddRoom = counsel.sessions?.sketching?.status === 'STARTED';

  /*
   * 모든 BottomSheet/Dialog 가 떠 있을 때는 underlying footer CTA(+ 방 추가하기)
   * 를 숨긴다 — 사용자 정책: "모든 바텀시트는 그 이전의 CTA 를 숨겨야 한다".
   *
   * BottomSheet 자체가 backdrop(rgba(0,0,0,0.15)) 을 덮긴 하지만, 시트 내부의
   * primary CTA 와 페이지의 primary CTA 가 함께 보이면 시각적 충돌이 생기고
   * 어느 버튼이 활성인지 모호해진다 (포커스 트랩은 시트 내부에 갇히지만 시각적
   * 노이즈가 남음). 따라서 시트 열림 동안 footer 를 display:none 처리한다.
   */
  const anySheetOpen = addSheetOpen || nameSheetOpen || removeConfirmEntry !== null;

  // ── 폴링 헬퍼 ──────────────────────────────────────────────────────────────
  /**
   * `refetch` 를 반복 호출해 SWR 캐시가 최신 프로젝션으로 동기화될 때까지 기다린다.
   * 매 사이클마다 새 counsel 이 prop 으로 흘러들어와 sections 가 자동 갱신되며,
   * predicate 가 true 가 되거나 deadline 에 도달하면 종료한다.
   */
  const pollUntilSynced = useCallback(
    async (predicate: (next: VisitCounselView) => boolean): Promise<void> => {
      const deadline = Date.now() + POLL_MAX_MS;
      while (Date.now() < deadline) {
        try {
          const result = await refetch();
          const next = result?.counsel;
          if (next && predicate(next)) return;
        } catch {
          // 일시적 네트워크 오류는 무시하고 다음 사이클로
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
    },
    [refetch]
  );

  // ── 방 추가 ───────────────────────────────────────────────────────────────
  const openAddSheet = useCallback(() => {
    setAddType(null);
    setAddSheetOpen(true);
  }, []);

  const performAddRoom = useCallback(
    async (type: RoomTypeValue, namePrefix: string) => {
      if (isAddingRoom) return;
      const previousCount = sketchRooms.length;
      setIsAddingRoom(true);
      try {
        await myVisitCounselApi.addSketchingRoom(visitCounselId, {
          type,
          namePrefix,
        });
        showToast('방이 추가되었습니다', 'success');
        // 프로젝션 반영을 짧게 기다려 sections 가 즉시 갱신되도록 한다.
        await pollUntilSynced((next) => {
          const rooms = next.sessions?.sketching?.rooms ?? [];
          const grew = rooms.length > previousCount;
          const matched = rooms.some((r) => r.type === type && r.namePrefix === namePrefix);
          return grew && matched;
        });
      } catch (err) {
        showToast(err instanceof Error ? err.message : '방 추가에 실패했습니다', 'error');
      } finally {
        setIsAddingRoom(false);
        setAddSheetOpen(false);
        setNameSheetOpen(false);
        setAddType(null);
        setNameInput('');
      }
    },
    [isAddingRoom, sketchRooms.length, visitCounselId, showToast, pollUntilSynced]
  );

  const confirmAddType = useCallback(() => {
    if (!addType) return;
    if (addType === 'OTHER_SPACE') {
      setAddSheetOpen(false);
      setNameInput('');
      setNameSheetOpen(true);
      return;
    }
    void performAddRoom(addType, labelForType(addType) ?? addType);
  }, [addType, performAddRoom]);

  const confirmAddName = useCallback(() => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    void performAddRoom('OTHER_SPACE', trimmed);
  }, [nameInput, performAddRoom]);

  // ── 방 삭제 ───────────────────────────────────────────────────────────────
  const handleRemoveConfirm = useCallback(
    async (entry: ObservingPickerEntry) => {
      const roomId = resolveRoomIdForDelete(entry);
      if (!roomId) return;
      setRemoveConfirmEntry(null);
      setRemovingRoomIds((prev) => {
        const next = new Set(prev);
        next.add(roomId);
        return next;
      });
      try {
        await myVisitCounselApi.removeSketchingRoom(visitCounselId, roomId);
        showToast('방이 제거되었습니다', 'success');
        await pollUntilSynced((next) => {
          const rooms = next.sessions?.sketching?.rooms ?? [];
          const observing = next.sessions?.roomObserving ?? [];
          const additional = next.sessions?.additionalSpaceObserving ?? [];
          const stillExists =
            rooms.some((r) => r.roomId === roomId) ||
            observing.some((s) => s.roomId === roomId) ||
            additional.some((s) => s.roomId === roomId);
          return !stillExists;
        });
      } catch (err) {
        showToast(err instanceof Error ? err.message : '방 제거에 실패했습니다', 'error');
      } finally {
        setRemovingRoomIds((prev) => {
          const next = new Set(prev);
          next.delete(roomId);
          return next;
        });
      }
    },
    [visitCounselId, showToast, pollUntilSynced]
  );

  // ── 렌더 ──────────────────────────────────────────────────────────────────
  return (
    <Box
      ref={wrapperRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'white',
        overflow: 'hidden',
      }}
    >
      {/* AppBar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 56,
          px: 1.5,
          flexShrink: 0,
        }}
      >
        <IconButton onClick={onBack} size="small" aria-label="뒤로가기">
          <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: '#404040',
            letterSpacing: '-0.02em',
            ml: 0.5,
          }}
        >
          방 수정하기
        </Typography>
      </Box>

      {/* 콘텐츠 */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 4.5,
          pt: 3,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {sections.length === 0 && (
          <Typography sx={{ fontSize: 14, color: '#9CA3AF', letterSpacing: '-0.02em' }}>
            표시할 방이 없어요. 아래에서 방을 추가해주세요.
          </Typography>
        )}

        {sections.map((section) => (
          <SectionGroup
            key={section.id}
            section={section}
            removingRoomIds={removingRoomIds}
            onRequestRemove={(entry) => setRemoveConfirmEntry(entry)}
          />
        ))}
      </Box>

      {/* Footer — "+ 방 추가하기" — 시트/다이얼로그 열림 시 숨김 */}
      {!anySheetOpen && (
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 'calc(env(safe-area-inset-bottom) + 16px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flexShrink: 0,
            bgcolor: 'white',
          }}
        >
          <Button
            variant="primary"
            size="l"
            width="fixed"
            onPress={openAddSheet}
            disabled={!canAddRoom || isAddingRoom}
          >
            {isAddingRoom ? '처리 중...' : '+ 방 추가하기'}
          </Button>
          {!canAddRoom && (
            <Typography
              sx={{
                fontSize: 12,
                color: '#9CA3AF',
                textAlign: 'center',
                letterSpacing: '-0.02em',
              }}
            >
              지금은 방을 추가할 수 없어요
            </Typography>
          )}
        </Box>
      )}

      <AddRoomTypeSheet<RoomTypeValue>
        open={addSheetOpen}
        options={ADD_ROOM_TYPE_OPTIONS}
        selected={addType}
        isSubmitting={isAddingRoom}
        onSelect={setAddType}
        onConfirm={confirmAddType}
        onClose={() => setAddSheetOpen(false)}
        container={wrapperRef.current}
      />

      <AddRoomNameSheet
        open={nameSheetOpen}
        value={nameInput}
        isSubmitting={isAddingRoom}
        placeholder="예: 다용도실"
        onChange={setNameInput}
        onConfirm={confirmAddName}
        onClose={() => setNameSheetOpen(false)}
        container={wrapperRef.current}
      />

      {/* 방 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        open={!!removeConfirmEntry}
        title="방을 제거할까요?"
        description={
          removeConfirmEntry
            ? `${removeConfirmEntry.title}을(를) 목록에서 제거합니다.\n작업이 끝난 뒤에는 다시 추가해야 해요.`
            : ''
        }
        confirmLabel="제거"
        onConfirm={() => {
          if (removeConfirmEntry) void handleRemoveConfirm(removeConfirmEntry);
        }}
        onCancel={() => setRemoveConfirmEntry(null)}
        container={wrapperRef.current}
      />
    </Box>
  );
}

export default RoomEditFlowAdapter;

// ── Subcomponents ────────────────────────────────────────────────────────────

interface SectionGroupProps {
  section: ObservingPickerSection;
  removingRoomIds: ReadonlySet<string>;
  onRequestRemove: (entry: ObservingPickerEntry) => void;
}

function SectionGroup({ section, removingRoomIds, onRequestRemove }: SectionGroupProps) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: '#9CA3AF',
          letterSpacing: '-0.02em',
          mb: 1,
        }}
      >
        {section.title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {section.entries.map((entry) => {
          const roomId = resolveRoomIdForDelete(entry);
          const removing = roomId ? removingRoomIds.has(roomId) : false;
          return (
            <RoomRow
              key={entry.itemId}
              entry={entry}
              removing={removing}
              onRequestRemove={() => onRequestRemove(entry)}
            />
          );
        })}
      </Box>
    </Box>
  );
}

interface RoomRowProps {
  entry: ObservingPickerEntry;
  removing: boolean;
  onRequestRemove: () => void;
}

function RoomRow({ entry, removing, onRequestRemove }: RoomRowProps) {
  const deletable = canDeleteObservingRoom(entry);
  const blockedReason = describeDeleteBlockedReason(entry);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        borderBottom: '1px solid #f3f4f6',
        opacity: removing ? 0.5 : 1,
      }}
    >
      <Box sx={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{entry.iconEmoji}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 16,
            color: '#111827',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            lineHeight: 1.3,
          }}
        >
          {entry.title}
        </Typography>
        {!deletable && blockedReason && (
          <Typography
            sx={{
              fontSize: 12,
              color: '#9CA3AF',
              letterSpacing: '-0.02em',
              mt: 0.25,
            }}
          >
            {blockedReason}
          </Typography>
        )}
      </Box>
      {removing ? (
        <CircularProgress size={18} />
      ) : deletable ? (
        <IconButton
          onClick={onRequestRemove}
          size="small"
          aria-label={`${entry.title} 제거`}
          sx={{ color: '#9CA3AF' }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
        </IconButton>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            color: '#D1D5DB',
          }}
          aria-label={`${entry.title} 잠김`}
        >
          <LockOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
      )}
    </Box>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function labelForType(value: RoomTypeValue): string | undefined {
  return ROOM_TYPES.find((r) => r.value === value)?.label;
}
